import { useState, useEffect, useCallback } from 'react';
import {
  getCleanupStatus,
  runCleanupNow,
  updateCleanupRetention,
  purgeNow,
  CleanupStatus,
  CleanupRetentionUpdate,
  PurgeTarget,
  PurgeSummary,
} from '../../services/admin';
import EffectiveRetentionList from './EffectiveRetentionList';
import { RETENTION_CATEGORIES, retentionChanges, retentionForm, validateRetentionChanges } from './cleanupRetention';

const resultStyle: Record<string, string> = {
  success: 'text-green-300 bg-green-900/20 border-green-800',
  partial: 'text-yellow-300 bg-yellow-900/20 border-yellow-800',
  failed: 'text-red-300 bg-red-900/20 border-red-700',
  skipped: 'text-gray-400 bg-gray-800/40 border-gray-600',
  running: 'text-blue-300 bg-blue-900/20 border-blue-800',
};

const fmt = (v?: string) => (v ? new Date(v).toLocaleString() : '—');

const PURGE_TARGETS: { value: PurgeTarget; label: string; description: string }[] = [
  { value: 'recordings', label: 'Recordings', description: 'Expires every recording\'s audio file immediately (metadata rows are kept, marked "expired").' },
  { value: 'logs', label: 'Logs', description: 'Clears the operational event log, system logs, session logs, floor-control audit log, and sync log.' },
  { value: 'temp_files', label: 'Temp files', description: 'Removes abandoned in-progress recording/export files - never touches finalized audio.' },
  { value: 'exports', label: 'Conversation exports', description: 'Deletes generated conversation export files (regenerated on next download request).' },
  { value: 'direct_messages', label: 'Direct messages', description: 'Permanently deletes all persisted Management Client and radio-to-radio messages.' },
  { value: 'backups', label: 'Backups', description: 'Deletes every backup file on disk (JSON and Postgres .dump).' },
];

const PURGE_CONFIRM_WORD = 'DELETE';

/** Retention/cleanup status, a manual "Run cleanup now" trigger, and an editable retention
 * form. Global (not per-network) - see documentation/architecture.md "Cleanup & retention". Editing
 * persists via the same system_config override mechanism as the rest of System Settings and
 * takes effect on the next run without a restart (see documentation/decisions.md). */
export default function CleanupManager() {
  const [status, setStatus] = useState<CleanupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [purgeTargets, setPurgeTargets] = useState<Set<PurgeTarget>>(new Set());
  const [purging, setPurging] = useState(false);
  const [purgeSummary, setPurgeSummary] = useState<PurgeSummary | null>(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<CleanupRetentionUpdate>({});
  const [initialForm, setInitialForm] = useState<CleanupRetentionUpdate>({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      setStatus(await getCleanupStatus());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cleanup status');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      if (!editing) load(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [load, editing]);

  const startEditing = () => {
    if (!status) return;
    const initial = retentionForm(status);
    setInitialForm(initial);
    setForm(initial);
    setEditing(true);
  };

  const handleSave = async () => {
    const changes = retentionChanges(initialForm, form);
    const invalid = validateRetentionChanges(changes);
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (Object.keys(changes).length > 0) await updateCleanupRetention(changes);
      await load();
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save retention settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!confirm('Run cleanup now, using the current retention configuration?')) return;
    setRunning(true);
    setError(null);
    try {
      await runCleanupNow();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run cleanup');
    } finally {
      setRunning(false);
    }
  };

  const togglePurgeTarget = (target: PurgeTarget) => {
    setPurgeTargets((prev) => {
      const next = new Set(prev);
      if (next.has(target)) next.delete(target);
      else next.add(target);
      return next;
    });
  };

  const toggleAllPurgeTargets = () => {
    setPurgeTargets((prev) => (prev.size === PURGE_TARGETS.length ? new Set() : new Set(PURGE_TARGETS.map((t) => t.value))));
  };

  const openPurgeConfirm = () => {
    if (purgeTargets.size === 0) return;
    setPurgeConfirmText('');
    setShowPurgeConfirm(true);
  };

  const handlePurgeNow = async () => {
    setPurging(true);
    setError(null);
    try {
      const summary = await purgeNow([...purgeTargets]);
      setPurgeSummary(summary);
      setShowPurgeConfirm(false);
      setPurgeTargets(new Set());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to purge');
    } finally {
      setPurging(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }
  if (!status) {
    return <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg p-3">{error ?? 'No data'}</div>;
  }

  const displayResult = status.is_running ? 'running' : status.last_result ?? '—';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">🧹 Cleanup &amp; Retention</h2>
        <button
          onClick={handleRunNow}
          disabled={running || status.is_running || !status.enabled}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white font-bold py-2 px-4 rounded"
        >
          {running || status.is_running ? 'Running…' : 'Run cleanup now'}
        </button>
      </div>

      {error && <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg p-3">{error}</div>}
      {saveSuccess && <div className="bg-green-900/30 border border-green-500 text-green-300 rounded-lg p-3 text-sm">✓ Retention settings saved.</div>}

      {!status.enabled && (
        <div className="bg-yellow-900/20 border border-yellow-700 text-yellow-300 rounded-lg p-3 text-sm">
          Cleanup is disabled (CLEANUP_ENABLED=false) - no scheduled or manual runs will occur.
        </div>
      )}
      {status.dry_run && (
        <div className="bg-blue-900/20 border border-blue-700 text-blue-300 rounded-lg p-3 text-sm">
          Dry-run mode is active (CLEANUP_DRY_RUN=true) - runs report what would be removed without changing anything.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 space-y-2">
          <h3 className="font-semibold text-gray-300">Last run</h3>
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-400">Started</span><span>{fmt(status.last_started_at)}</span></div>
            <div className="flex justify-between"><span className="text-gray-400">Completed</span><span>{fmt(status.last_completed_at)}</span></div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Result</span>
              <span className={`text-xs px-2 py-0.5 rounded border ${resultStyle[displayResult] ?? ''}`}>{displayResult}</span>
            </div>
            {status.last_error && <div className="text-red-400 text-xs mt-1">{status.last_error}</div>}
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 space-y-2">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-300">Effective retention</h3>
            {!editing ? (
              <button onClick={startEditing} className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded">
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  className="text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-40 px-2 py-1 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-40 px-2 py-1 rounded"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>

          {!editing ? (
            <EffectiveRetentionList status={status} />
          ) : (
            <div className="text-sm space-y-2">
              {RETENTION_CATEGORIES.map((c) => (
                <RetentionField
                  key={c.key}
                  label={`${c.label} (${c.unit}${c.max ? `, 1-${c.max}` : ''})`}
                  value={form[c.key]}
                  max={c.max}
                  onChange={(v) => setForm({ ...form, [c.key]: v })}
                />
              ))}
              <RetentionField label="Scheduled interval (hours)" value={form.interval_hours} onChange={(v) => setForm({ ...form, interval_hours: v })} />
              <RetentionField label="Batch size" value={form.batch_size} onChange={(v) => setForm({ ...form, batch_size: v })} />
              <label className="flex justify-between items-center pt-1">
                <span className="text-gray-400">Enabled</span>
                <input type="checkbox" checked={form.enabled ?? false} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              </label>
              <label className="flex justify-between items-center">
                <span className="text-gray-400">Dry run</span>
                <input type="checkbox" checked={form.dry_run ?? false} onChange={(e) => setForm({ ...form, dry_run: e.target.checked })} />
              </label>
              <p className="text-xs text-gray-500 pt-1">
                Retention/interval/batch values apply to the next run immediately; the scheduled interval itself takes effect after a server restart.
                Only changed values are saved; a category kept forever (0, set in the environment) stays so unless changed here.
              </p>
            </div>
          )}
        </div>
      </div>

      {status.last_summary && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <h3 className="font-semibold text-gray-300 mb-3">Last run summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Stat label="Recordings expired" value={status.last_summary.recordings_expired} />
            <Stat label="Recording file errors" value={status.last_summary.recording_file_errors} warn />
            <Stat label="Exports removed" value={status.last_summary.conversation_exports_removed} />
            <Stat label="Temp files removed" value={status.last_summary.temp_files_removed} />
            <Stat label="Log rows removed" value={status.last_summary.log_rows_removed} />
            <Stat label="Location history rows removed" value={status.last_summary.location_history_rows_removed ?? 0} />
            <Stat label="Orphan files removed" value={status.last_summary.orphan_files_removed} />
            <Stat label="Stale recordings recovered" value={status.last_summary.stale_recordings_recovered} />
            <Stat label="Stale recoveries failed" value={status.last_summary.stale_recordings_failed} warn />
            <Stat label="Missing files reconciled" value={status.last_summary.missing_files_reconciled} />
            <Stat label="Duration" value={`${status.last_summary.duration_ms} ms`} />
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg border border-red-900/60 p-4 space-y-3">
        <div>
          <h3 className="font-semibold text-gray-200">🗑️ Delete everything</h3>
          <p className="text-xs text-gray-400 mt-1">
            Immediately deletes accumulated/disposable data, regardless of the retention settings above - a "fresh
            start" for the system. Never touches organizations, users, radios, groups, API keys, or provisioning -
            and recording metadata rows are kept (only the audio file is removed), so history/context is preserved.
            Location history (GPS) is not offered here: it only expires through its retention period, which always keeps
            each radio's latest position.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-300 pb-1 border-b border-gray-700">
          <input
            type="checkbox"
            checked={purgeTargets.size === PURGE_TARGETS.length}
            onChange={toggleAllPurgeTargets}
          />
          Select all
        </label>

        <div className="space-y-2">
          {PURGE_TARGETS.map((t) => (
            <label key={t.value} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={purgeTargets.has(t.value)}
                onChange={() => togglePurgeTarget(t.value)}
                className="mt-0.5"
              />
              <span>
                <span className="text-gray-200 font-medium">{t.label}</span>
                <span className="text-gray-500"> — {t.description}</span>
              </span>
            </label>
          ))}
        </div>

        <button
          onClick={openPurgeConfirm}
          disabled={purging || purgeTargets.size === 0}
          className="bg-red-700 hover:bg-red-600 disabled:opacity-40 text-white font-bold py-2 px-4 rounded text-sm"
        >
          {purging ? 'Deleting…' : `Delete selected (${purgeTargets.size})`}
        </button>

        {purgeSummary && (
          <div className={`rounded-lg p-3 text-sm border ${resultStyle[purgeSummary.result] ?? ''}`}>
            <p className="font-semibold mb-2">Purge {purgeSummary.result} ({purgeSummary.duration_ms} ms)</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              {purgeSummary.recordings_expired !== undefined && <span>Recordings expired: {purgeSummary.recordings_expired}</span>}
              {purgeSummary.recording_file_errors !== undefined && <span>Recording file errors: {purgeSummary.recording_file_errors}</span>}
              {purgeSummary.event_log_rows_removed !== undefined && <span>Event log rows: {purgeSummary.event_log_rows_removed}</span>}
              {purgeSummary.system_log_rows_removed !== undefined && <span>System log rows: {purgeSummary.system_log_rows_removed}</span>}
              {purgeSummary.session_log_rows_removed !== undefined && <span>Session log rows: {purgeSummary.session_log_rows_removed}</span>}
              {purgeSummary.floor_event_rows_removed !== undefined && <span>Floor event rows: {purgeSummary.floor_event_rows_removed}</span>}
              {purgeSummary.sync_log_rows_removed !== undefined && <span>Sync log rows: {purgeSummary.sync_log_rows_removed}</span>}
              {purgeSummary.temp_files_removed !== undefined && <span>Temp files removed: {purgeSummary.temp_files_removed}</span>}
              {purgeSummary.conversation_exports_removed !== undefined && <span>Exports removed: {purgeSummary.conversation_exports_removed}</span>}
              {purgeSummary.direct_messages_removed !== undefined && <span>Direct messages removed: {purgeSummary.direct_messages_removed}</span>}
              {purgeSummary.backups_removed !== undefined && <span>Backups removed: {purgeSummary.backups_removed}</span>}
              {purgeSummary.backup_errors !== undefined && <span>Backup errors: {purgeSummary.backup_errors}</span>}
            </div>
          </div>
        )}
      </div>

      {showPurgeConfirm && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-red-800 w-full max-w-md p-6 space-y-4">
            <h3 className="text-xl font-bold text-red-300">⚠️ Confirm permanent deletion</h3>
            <p className="text-sm text-gray-300">
              You're about to delete: <span className="font-semibold">{[...purgeTargets].map((t) => PURGE_TARGETS.find((p) => p.value === t)?.label).join(', ')}</span>.
              This cannot be undone.
            </p>
            <label className="block">
              <p className="text-sm text-gray-400 mb-1">Type <span className="font-mono text-red-300">{PURGE_CONFIRM_WORD}</span> to confirm</p>
              <input
                value={purgeConfirmText}
                onChange={(e) => setPurgeConfirmText(e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white font-mono"
                autoFocus
              />
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={() => setShowPurgeConfirm(false)}
                disabled={purging}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white font-semibold rounded"
              >
                Cancel
              </button>
              <button
                onClick={handlePurgeNow}
                disabled={purging || purgeConfirmText !== PURGE_CONFIRM_WORD}
                className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-40 text-white font-semibold rounded"
              >
                {purging ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: number | string; warn?: boolean }) {
  return (
    <div className="bg-gray-900 rounded p-3">
      <p className="text-gray-400 text-xs">{label}</p>
      <p className={`text-xl font-bold ${warn && Number(value) > 0 ? 'text-red-400' : ''}`}>{value}</p>
    </div>
  );
}

function RetentionField({ label, value, max, onChange }: { label: string; value?: number; max?: number; onChange: (v: number) => void }) {
  return (
    <label className="flex justify-between items-center gap-3">
      <span className="text-gray-400">{label}</span>
      <input
        type="number"
        min={1}
        max={max}
        value={value ?? ''}
        onChange={(e) => onChange(Math.min(max ?? Number.MAX_SAFE_INTEGER, Math.max(1, parseInt(e.target.value, 10) || 1)))}
        className="w-24 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-right"
      />
    </label>
  );
}
